import {
  ChangeEvent,
  FocusEventHandler,
  ReactElement,
  useContext,
  useState,
} from 'react';
import {useTranslation} from 'react-i18next';
import ICON_ARROW_UP_DARK from '../../../public/static/v4/comment/icon-arrow-up-dark.svg';
import ICON_ARROW_UP_GREY from '../../../public/static/v4/comment/icon-arrow-up-grey.svg';
import ICON_ARROW_UP_LIGHT from '../../../public/static/v4/comment/icon-arrow-up-light.svg';
import Button from '../../common/component/Button';
import {pToast} from '../../common/container/PToast';
import {showAuthModal} from '../../common/function';
import useLogin from '../../common/hook/useLogin';
import useTheme from '../../common/hook/useTheme';
import {CommentThemeContext} from '../common';
import useCommentEvent from '../hook/useCommentEvent';
import {ICommentInputVariant, IIconTheme} from '../type';

interface IProps {
  variant?: ICommentInputVariant;
  placeholder?: string;
  isReply: boolean;
  spoilerElement?: ReactElement;
  onClickPost: (content: string) => void;
  onChangeContent?: (content: string) => void;
}

export default function CommentInput({
  variant = 'darkGrey',
  placeholder,
  isReply,
  spoilerElement,
  onClickPost,
  onChangeContent,
}: IProps) {
  const {t} = useTranslation();
  const [content, setContent] = useState('');
  const [showPlaceHolder, setShowPlaceHoler] = useState(true);
  const {isLogin} = useLogin();
  const {theme} = useTheme();
  const {inputWrapperStyle: _inputWrapperStyle, inputWrapperStyleForReply} =
    useContext(CommentThemeContext);

  const inputWrapperStyle = isReply
    ? inputWrapperStyleForReply
    : _inputWrapperStyle;
  const iconTheme = variant === 'white' ? 'white' : theme;
  const isActive = content.length > 0;

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const text = e.currentTarget.value;
    // 기본 인코딩(utf-16) 기준 글자 수로 처리한다.(라인웹툰 동일)
    if (text.length > MAX_LENGTH_BYTE) {
      pToast(t('login_comment_exceed_toast'));
    } else {
      setContent(text);
      onChangeContent?.(text);
    }
  };

  const {sendCommentEvent} = useCommentEvent();
  const onClickPostBtn = () => {
    onClickPost(content);
    setContent('');
    sendCommentEvent({actionName: 'comment_enter', action: 'click'});
  };

  const onFocus: FocusEventHandler<HTMLInputElement> = e => {
    if (isLogin) {
      setShowPlaceHoler(false);
    } else {
      e.currentTarget.blur();
      showAuthModal({isSaveReturnPage: true});
    }
  };

  const isWhite = variant === 'white';
  return (
    <div
      className={cn(
        'w-full rounded-8 border border-solid py-10 pl-12 pr-10 use-gpu',
        inputWrapperStyle
      )}
    >
      {spoilerElement}
      <div className="flex">
        <input
          className={cn(
            'bg-transparent',
            'leading-20 mr-8 border-none outline-none min-w-[184px] flex-auto',
            isWhite
              ? isReply
                ? 's14-regular-white placeholder-white/30'
                : 's14-regular-grey01 placeholder-grey-10 light:text-grey-15 light:placeholder-grey-06'
              : 's14-regular-grey15 placeholder-grey-06'
          )}
          type="text"
          value={content}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={() => setShowPlaceHoler(true)}
          placeholder={
            showPlaceHolder
              ? placeholder ?? t('comment_before_enter')
              : undefined
          }
        />
        <Button
          className={cn(
            'w-28 h-28 rounded-5 flex-center',
            getEnterIconBackgrounCN[iconTheme](isReply)
          )}
          onClick={onClickPostBtn}
          disabled={!content.length}
        >
          <img
            src={getEnterIconMap[iconTheme]({
              isReply,
              isActive,
            })}
            alt="submit"
          />
        </Button>
      </div>
    </div>
  );
}

const MAX_LENGTH_BYTE = 600;

const getEnterIconBackgrounCN: Record<
  IIconTheme,
  (isReply: boolean) => string
> = {
  dark: () => 'bg-white disabled:bg-grey-04',
  light: () => 'bg-black disabled:bg-grey-04',
  white: isReply =>
    isReply
      ? 'bg-white disabled:bg-white/20'
      : 'bg-black disabled:bg-grey-12 light:disabled:bg-grey-04',
};

const getEnterIconMap: Record<
  IIconTheme,
  ({isReply, isActive}: {isReply: boolean; isActive: boolean}) => string
> = {
  dark: () => ICON_ARROW_UP_DARK,
  light: () => ICON_ARROW_UP_LIGHT,
  white: ({isReply, isActive}) =>
    isReply
      ? isActive
        ? ICON_ARROW_UP_DARK
        : ICON_ARROW_UP_GREY
      : ICON_ARROW_UP_LIGHT,
};
